import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from "react";
import { Loader, Menu, SegmentedControl, Slider, notifications, InstrumentalConfirmModal, RenameAudioModal, } from "@/shared/ui";
import { useReportPlayerBarHeight } from "@/shared/lib/playerBarHeight";
import { IconPlayerPlay, IconPlayerPause, IconPlayerSkipBack, IconPlayerSkipForward, IconMicrophone, IconSend, IconDots, IconX, } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { usePlayer } from "@/app/providers/PlayerProvider";
import { isTelegramMiniApp, isMobileDevice } from "@/shared/lib/telegram";
import { isMaxMiniApp } from "@/shared/lib/max";
import { downloadAudio, downloadInstrumental, downloadWav, } from "@/shared/lib/downloadAudio";
import { requestInstrumental, getInstrumentalStatus, waitForInstrumental, requestWav, getWavStatus, waitForWav, renameAudio, getAudioPublicToken, } from "@/shared/api/songs";
import { buildTrackActions, kindFromAudioType, useTrackActions, } from "@/features/track-actions";
import styles from "./WebPlayer.module.scss";
// Безопасный набор действий для треков БЕЗ available_actions (чарты/шаринг —
// прав владельца нет). Совпадает с прежним меню плеера: без избранного/чарта/
// скрыть/удалить/повторить/ссылок/ИИ-голоса.
const PLAYER_FALLBACK_ACTIONS = [
    "download_mp3",
    "download_wav",
    "rename",
    "make_instrumental",
    "extend",
    "video_cover",
    "karaoke",
];
export const WebPlayer = ({ language, fullWidth }) => {
    const navigate = useNavigate();
    const { isPlayerOpen, currentTrack, isPlaying, currentTime, duration, isAudioLoading, playMode, setPlayMode, togglePlayPause, setCurrentTime, audioRef, nextTrack, prevTrack, patchCurrentTrack, closePlayer, } = usePlayer();
    // Публикуем высоту бара в --vp-player-h, чтобы контент/поля ввода резервировали
    // место снизу (см. useReportPlayerBarHeight).
    const barRef = useRef(null);
    useReportPlayerBarHeight(barRef, isPlayerOpen && !!currentTrack);
    const [failedImages, setFailedImages] = useState(new Set());
    const [instrLoading, setInstrLoading] = useState(false);
    const [wavLoading, setWavLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const [minusResolving, setMinusResolving] = useState(false);
    const [scrollbarWidth, setScrollbarWidth] = useState(0);
    const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
    const isMiniApp = isTelegramMiniApp() ||
        isMaxMiniApp() ||
        isMobileDevice() ||
        windowWidth <= 770;
    const SIDEBAR_WIDTH_DESKTOP = 280; // совпадает с шириной .sidebar
    // Вторичные действия (избранное / чарт / скрыть / удалить / повторить /
    // ссылки на подарок / ИИ-голос) — единый контроллер, чтобы меню плеера
    // совпадало с меню списка без дублирования логики. Патчим сам currentTrack
    // (оптимистично), удаление закрывает плеер.
    const secondary = useTrackActions({
        language,
        audioId: currentTrack?.audioId,
        songId: currentTrack?.songId,
        title: currentTrack?.title,
        favorited: currentTrack?.favorited,
        isShow: currentTrack?.isShow,
        isShowInCharts: currentTrack?.isShowInCharts,
        onPatch: (patch) => {
            if (currentTrack?.audioId != null)
                patchCurrentTrack(patch, currentTrack.audioId);
        },
        onDeleted: () => closePlayer(),
    });
    useEffect(() => {
        const calculateScrollbarWidth = () => {
            const outer = document.createElement("div");
            outer.style.visibility = "hidden";
            outer.style.overflow = "scroll";
            outer.style.msOverflowStyle = "scrollbar";
            document.body.appendChild(outer);
            const inner = document.createElement("div");
            outer.appendChild(inner);
            const width = outer.offsetWidth - inner.offsetWidth;
            outer.parentNode?.removeChild(outer);
            setScrollbarWidth(width);
        };
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            calculateScrollbarWidth();
        };
        calculateScrollbarWidth();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    // Синхронизация слайдера с audio элементом
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio)
            return;
        const syncProgress = () => {
            if (audio.duration) {
                setCurrentTime((audio.currentTime / audio.duration) * 100);
            }
        };
        audio.addEventListener("timeupdate", syncProgress);
        audio.addEventListener("seeked", syncProgress);
        return () => {
            audio.removeEventListener("timeupdate", syncProgress);
            audio.removeEventListener("seeked", syncProgress);
        };
    }, [audioRef, setCurrentTime]);
    const formatTime = (progress, durationString) => {
        if (!durationString || durationString === "0:00") {
            if (duration > 0) {
                const totalSeconds = Math.floor(duration);
                const currentSeconds = Math.floor((progress / 100) * totalSeconds);
                const currentMinutes = Math.floor(currentSeconds / 60);
                const currentSecondsRemainder = currentSeconds % 60;
                return `${currentMinutes}:${String(currentSecondsRemainder).padStart(2, "0")}`;
            }
            return "0:00";
        }
        const [minutes, seconds] = durationString.split(":").map(Number);
        const totalSeconds = minutes * 60 + seconds;
        const currentSeconds = Math.floor((progress / 100) * totalSeconds);
        const currentMinutes = Math.floor(currentSeconds / 60);
        const currentSecondsRemainder = currentSeconds % 60;
        return `${currentMinutes}:${String(currentSecondsRemainder).padStart(2, "0")}`;
    };
    const formatDuration = () => {
        if (currentTrack?.duration && currentTrack.duration !== "0:00") {
            return currentTrack.duration;
        }
        if (duration > 0) {
            const totalSeconds = Math.floor(duration);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            return `${minutes}:${String(seconds).padStart(2, "0")}`;
        }
        return "0:00";
    };
    const handleSeek = (value) => {
        setCurrentTime(value);
        if (audioRef.current && audioRef.current.duration) {
            audioRef.current.currentTime = (value / 100) * audioRef.current.duration;
        }
    };
    const handleShare = async () => {
        const url = currentTrack?.audioUrl;
        if (!url)
            return;
        try {
            await navigator.clipboard.writeText(url);
            notifications.show({
                title: language === "ru" ? "Ссылка скопирована" : "Link copied",
                message: "",
                color: "green",
            });
        }
        catch {
            notifications.show({
                title: language === "ru" ? "Не удалось скопировать" : "Could not copy",
                message: "",
                color: "red",
            });
        }
    };
    // Клик по названию → страница песни (публичный хаб). Резолвим by-token лениво
    // (создаётся идемпотентно на бэке) и переходим. Мягко игнорируем ошибку —
    // название просто не кликнется (напр. у трека нет audioId).
    const [titleNavigating, setTitleNavigating] = useState(false);
    const handleTitleClick = async () => {
        const audioId = currentTrack?.audioId;
        if (audioId == null || titleNavigating)
            return;
        setTitleNavigating(true);
        try {
            const publicToken = await getAudioPublicToken(audioId);
            navigate(`/song/${publicToken}`);
        }
        catch (e) {
            console.error("WebPlayer: failed to resolve public token", e);
        }
        finally {
            setTitleNavigating(false);
        }
    };
    const handleDownload = () => {
        if (!currentTrack)
            return;
        void downloadAudio({
            audioId: currentTrack.audioId,
            audioUrl: currentTrack.audioUrl,
            title: currentTrack.title,
        });
    };
    // Переименование текущего трека (поварьянтно). Оптимистично меняем заголовок в
    // плеере, затем PATCH; при ошибке откатываемся и показываем уведомление.
    const handleRenameSubmit = async (rawTitle) => {
        const newTitle = rawTitle.trim();
        const audioId = currentTrack?.audioId;
        if (!newTitle || audioId == null)
            return;
        const prevTitle = currentTrack?.title ?? "";
        setRenameOpen(false);
        patchCurrentTrack({ title: newTitle }, audioId);
        try {
            const res = await renameAudio(audioId, newTitle);
            if (res.title && res.title !== newTitle) {
                patchCurrentTrack({ title: res.title }, audioId);
            }
        }
        catch (e) {
            console.error("renameAudio:", e);
            patchCurrentTrack({ title: prevTitle }, audioId);
            notifications.show({
                color: "red",
                title: language === "ru" ? "Не удалось переименовать" : "Rename failed",
                message: language === "ru"
                    ? "Попробуйте ещё раз чуть позже."
                    : "Please try again in a moment.",
            });
        }
    };
    // Минус готов, если статус ready и есть audioId (URL можем дорезолвить). Не
    // требуем instrumentalUrl в треке: история иногда приходит без него — тогда
    // тянем его on-demand при переключении на «Минус».
    const hasInstrumental = currentTrack?.instrumentalStatus === "ready" && Boolean(currentTrack?.audioId);
    // Переключение источника Песня/Минус. Для минуса нужен проигрываемый URL —
    // если в треке его ещё нет, дорезолвиваем через статус (источник истины) и
    // патчим трек в том же батче, что и смену режима.
    const handleModeChange = async (mode) => {
        if (mode === "full") {
            setPlayMode("full");
            return;
        }
        if (currentTrack?.instrumentalUrl) {
            setPlayMode("minus");
            return;
        }
        const audioId = currentTrack?.audioId;
        if (!audioId || minusResolving)
            return;
        setMinusResolving(true);
        try {
            const st = await getInstrumentalStatus(audioId);
            if (st.status === "ready" && st.url) {
                patchCurrentTrack({ instrumentalStatus: "ready", instrumentalUrl: st.url }, audioId);
                setPlayMode("minus");
            }
            else {
                notifications.show({
                    color: "red",
                    title: language === "ru" ? "Минус недоступен" : "Instrumental unavailable",
                    message: language === "ru"
                        ? "Не удалось получить ссылку на минус."
                        : "Couldn't fetch the instrumental link.",
                });
            }
        }
        catch {
            notifications.show({
                color: "red",
                title: language === "ru" ? "Ошибка" : "Error",
                message: language === "ru"
                    ? "Не удалось загрузить минус."
                    : "Failed to load the instrumental.",
            });
        }
        finally {
            setMinusResolving(false);
        }
    };
    // Готовый минус качаем сразу (без списания). Иначе — гейт с подтверждением:
    // создание минуса стоит 1 песню.
    const handleInstrumentalAction = () => {
        if (!currentTrack?.audioId || instrLoading)
            return;
        if (hasInstrumental) {
            void downloadInstrumental({
                audioId: currentTrack.audioId,
                title: currentTrack.title,
            });
            return;
        }
        setConfirmOpen(true);
    };
    // Реальный заказ минуса (после подтверждения). Готовность ловим через
    // WebSocket по ws_event_id (как генерацию песни), на таймаут/ошибку — догон
    // по API. requestInstrumental идемпотентен (ready → ссылка сразу, без списания).
    const startInstrumental = async () => {
        const audioId = currentTrack?.audioId;
        if (!audioId || instrLoading)
            return;
        const title = currentTrack?.title;
        setInstrLoading(true);
        // Минус готов: обновляем состояние трека (появляется тумблер «Минус» и
        // пункт «Скачать минус»), уведомляем и скачиваем. URL берём из ответа/статуса
        // (WS-колбэк его не передаёт) — источник истины.
        const applyReady = async (knownUrl) => {
            let url = knownUrl ?? null;
            if (!url) {
                try {
                    const st = await getInstrumentalStatus(audioId);
                    url = st.url ?? null;
                }
                catch {
                    /* игнорируем — минус всё равно готов, просто без прямого url */
                }
            }
            patchCurrentTrack({ instrumentalStatus: "ready", instrumentalUrl: url });
            notifications.show({
                title: language === "ru" ? "Минус готов" : "Instrumental ready",
                message: language === "ru"
                    ? "Можно послушать через переключатель или скачать."
                    : "Listen via the toggle or download it.",
            });
            void downloadInstrumental({ audioId, title });
        };
        let started;
        try {
            started = await requestInstrumental(audioId);
        }
        catch (e) {
            const status = e?.response?.status;
            if (status !== 402) {
                notifications.show({
                    color: "red",
                    title: language === "ru" ? "Ошибка" : "Error",
                    message: language === "ru"
                        ? "Не удалось заказать минус."
                        : "Failed to request instrumental.",
                });
            }
            setInstrLoading(false);
            return;
        }
        if (started.status === "ready" && started.url) {
            await applyReady(started.url);
            setInstrLoading(false);
            return;
        }
        if (!started.ws_event_id) {
            notifications.show({
                title: language === "ru" ? "Готовим минус" : "Preparing instrumental",
                message: language === "ru" ? "Загляните чуть позже." : "Check back a bit later.",
            });
            setInstrLoading(false);
            return;
        }
        notifications.show({
            title: language === "ru" ? "Готовим минус" : "Preparing instrumental",
            message: language === "ru"
                ? "Это займёт до пары минут, мы скачаем его автоматически."
                : "This can take a couple of minutes; the download will start automatically.",
        });
        waitForInstrumental(started.ws_event_id, async () => {
            await applyReady();
            setInstrLoading(false);
        }, async () => {
            try {
                const st = await getInstrumentalStatus(audioId);
                if (st.status === "ready") {
                    await applyReady(st.url);
                }
                else if (st.status === "failed") {
                    patchCurrentTrack({ instrumentalStatus: "failed", instrumentalUrl: null });
                    notifications.show({
                        color: "red",
                        title: language === "ru"
                            ? "Не удалось сделать минус"
                            : "Failed to make instrumental",
                        message: language === "ru"
                            ? "Средства возвращены. Попробуйте ещё раз."
                            : "Refunded. Please try again.",
                    });
                }
                else {
                    notifications.show({
                        title: language === "ru"
                            ? "Минус ещё готовится"
                            : "Instrumental still processing",
                        message: language === "ru" ? "Загляните чуть позже." : "Check back a bit later.",
                    });
                }
            }
            catch {
                /* игнорируем — просто снимаем индикатор */
            }
            setInstrLoading(false);
        });
    };
    // WAV готов, если статус ready и есть audioId. Как и минус — скачиваем сразу;
    // иначе (WAV бесплатен) сразу заказываем конвертацию БЕЗ подтверждения.
    const hasWav = currentTrack?.wavStatus === "ready" && Boolean(currentTrack?.audioId);
    const handleWavAction = () => {
        if (!currentTrack?.audioId || wavLoading)
            return;
        if (hasWav) {
            void downloadWav({
                audioId: currentTrack.audioId,
                title: currentTrack.title,
            });
            return;
        }
        void startWav();
    };
    // Реальный заказ WAV. Готовность ловим через WebSocket по ws_event_id, на
    // таймаут/ошибку — догон по API. Бесплатно и без подтверждения списания.
    const startWav = async () => {
        const audioId = currentTrack?.audioId;
        if (!audioId || wavLoading)
            return;
        const title = currentTrack?.title;
        setWavLoading(true);
        const applyReady = async (knownUrl) => {
            let url = knownUrl ?? null;
            if (!url) {
                try {
                    const st = await getWavStatus(audioId);
                    url = st.url ?? null;
                }
                catch {
                    /* игнорируем — WAV всё равно готов, просто без прямого url */
                }
            }
            patchCurrentTrack({ wavStatus: "ready", wavUrl: url });
            notifications.show({
                title: language === "ru" ? "WAV готов" : "WAV ready",
                message: language === "ru"
                    ? "Скачивание начнётся автоматически."
                    : "The download will start automatically.",
            });
            void downloadWav({ audioId, title });
        };
        let started;
        try {
            started = await requestWav(audioId);
        }
        catch {
            notifications.show({
                color: "red",
                title: language === "ru" ? "Ошибка" : "Error",
                message: language === "ru"
                    ? "Не удалось заказать WAV."
                    : "Failed to request WAV.",
            });
            setWavLoading(false);
            return;
        }
        if (started.status === "ready" && started.url) {
            await applyReady(started.url);
            setWavLoading(false);
            return;
        }
        patchCurrentTrack({ wavStatus: "processing" });
        if (!started.ws_event_id) {
            notifications.show({
                title: language === "ru" ? "Готовим WAV" : "Preparing WAV",
                message: language === "ru" ? "Загляните чуть позже." : "Check back a bit later.",
            });
            setWavLoading(false);
            return;
        }
        notifications.show({
            title: language === "ru" ? "Готовим WAV" : "Preparing WAV",
            message: language === "ru"
                ? "Это займёт немного времени, мы скачаем его автоматически."
                : "This can take a moment; the download will start automatically.",
        });
        waitForWav(started.ws_event_id, async () => {
            await applyReady();
            setWavLoading(false);
        }, async () => {
            try {
                const st = await getWavStatus(audioId);
                if (st.status === "ready") {
                    await applyReady(st.url);
                }
                else if (st.status === "failed") {
                    patchCurrentTrack({ wavStatus: "failed", wavUrl: null });
                    notifications.show({
                        color: "red",
                        title: language === "ru" ? "Не удалось сделать WAV" : "Failed to make WAV",
                        message: language === "ru" ? "Попробуйте ещё раз." : "Please try again.",
                    });
                }
                else {
                    notifications.show({
                        title: language === "ru" ? "WAV ещё готовится" : "WAV still processing",
                        message: language === "ru" ? "Загляните чуть позже." : "Check back a bit later.",
                    });
                }
            }
            catch {
                /* игнорируем — просто снимаем индикатор */
            }
            setWavLoading(false);
        });
    };
    if (!isPlayerOpen || !currentTrack)
        return null;
    const shareLabel = language === "ru" ? "Поделиться" : "Share";
    const audioId = currentTrack.audioId;
    // Данные для навигации в разделы создания (продление/видео/караоке).
    const navState = {
        audioId,
        title: currentTrack.title,
        audioUrl: currentTrack.audioUrl,
        imageUrl: currentTrack.imageUrl,
    };
    // Тип и набор действий трека. Из списка «Моей музыки» приходит
    // availableActions (владелец → полный набор с избранным/скрыть/удалить). У
    // лёгких источников (чарты, шаринг) прав владельца нет — фолбэк ограничен
    // безопасным набором без владельческих действий (как раньше в плеере).
    const trackKind = currentTrack.kind ?? kindFromAudioType(currentTrack.audioType ?? "normal");
    const isOwnedTrack = currentTrack.availableActions != null;
    const trackActions = currentTrack.availableActions ?? PLAYER_FALLBACK_ACTIONS;
    // Единая фабрика меню — тот же набор, что в списке песен и в инфо о песне.
    const menuItems = buildTrackActions({
        language,
        actions: trackActions,
        kind: trackKind,
        favorited: currentTrack.favorited,
        isShow: currentTrack.isShow,
        isShowInCharts: currentTrack.isShowInCharts,
        wavReady: hasWav,
        wavBusy: wavLoading || currentTrack.wavStatus === "processing",
        wavDisabled: wavLoading,
        instrReady: hasInstrumental,
        instrBusy: instrLoading,
        // В плеере готовый минус скачивается (в списке — показывается отдельной строкой).
        instrReadyMode: "download",
        repeatBusy: secondary.repeatBusy,
        showChartInMenu: true,
        canVoicePersona: isOwnedTrack &&
            trackKind === "song" &&
            audioId != null &&
            !!currentTrack.audioUrl,
        // «Релиз трека» — только владельцу готовой песни (у чужих/лёгких источников
        // плеера available_actions нет, значит и релизить нечего).
        canRelease: isOwnedTrack &&
            trackKind === "song" &&
            audioId != null &&
            !!currentTrack.audioUrl,
        onDownload: () => handleDownload(),
        onWav: audioId != null ? () => void handleWavAction() : undefined,
        onRename: audioId != null ? () => setRenameOpen(true) : undefined,
        onFavorite: audioId != null ? secondary.onFavorite : undefined,
        onChart: audioId != null ? secondary.onChart : undefined,
        onShare: audioId != null ? secondary.onShare : undefined,
        onMakeInstrumental: audioId != null ? () => void handleInstrumentalAction() : undefined,
        onVoicePersona: secondary.onVoicePersona,
        onRelease: audioId != null
            ? () => navigate(`/release?audioId=${audioId}`)
            : undefined,
        onExtend: audioId != null
            ? () => navigate("/extend-song", {
                state: { ...navState, duration: currentTrack.duration },
            })
            : undefined,
        onVideoCover: audioId != null
            ? () => navigate("/video-cover", { state: navState })
            : undefined,
        onKaraoke: audioId != null
            ? () => navigate("/karaoke", { state: navState })
            : undefined,
        onRepeat: secondary.onRepeat,
        onHide: audioId != null ? secondary.onHide : undefined,
        onDelete: audioId != null ? secondary.onDelete : undefined,
    });
    return (_jsxs("div", { ref: barRef, className: styles.bar, style: {
            bottom: isMiniApp ? "var(--miniapp-navbar-h, 64px)" : 0,
            left: isMiniApp || fullWidth ? 0 : SIDEBAR_WIDTH_DESKTOP,
            right: isMiniApp ? (scrollbarWidth > 0 ? scrollbarWidth : 0) : 0,
        }, children: [_jsxs("div", { className: styles.inner, children: [_jsxs("div", { className: styles.trackInfo, children: [_jsx("div", { className: styles.cover, children: currentTrack.imageUrl && !failedImages.has(currentTrack.id) ? (_jsx("img", { className: styles.coverImg, src: currentTrack.imageUrl, alt: currentTrack.title, onError: () => setFailedImages((prev) => new Set(prev).add(currentTrack.id)) })) : (_jsx(IconMicrophone, { size: 22, color: "var(--vp-text-dim)" })) }), _jsxs("div", { className: styles.titleStack, children: [currentTrack.audioId != null ? (_jsx("button", { type: "button", className: styles.trackTitleLink, onClick: () => void handleTitleClick(), title: language === "ru"
                                            ? "Открыть страницу песни"
                                            : "Open song page", children: currentTrack.title })) : (_jsx("span", { className: styles.trackTitle, children: currentTrack.title })), currentTrack.date && (_jsx("span", { className: styles.trackMeta, children: currentTrack.date }))] })] }), _jsxs("div", { className: styles.controls, children: [_jsx("button", { type: "button", className: `${styles.ctrlBtn} ${styles.prevNext}`, onClick: prevTrack, "aria-label": "Previous", children: _jsx(IconPlayerSkipBack, { size: 18 }) }), _jsx("button", { type: "button", className: `${styles.ctrlBtn} ${styles.playBtn}`, onClick: togglePlayPause, disabled: isAudioLoading, "aria-label": isPlaying ? "Pause" : "Play", children: isAudioLoading ? (_jsx(Loader, { size: "sm" })) : isPlaying ? (_jsx(IconPlayerPause, { size: 22 })) : (_jsx(IconPlayerPlay, { size: 22 })) }), _jsx("button", { type: "button", className: `${styles.ctrlBtn} ${styles.prevNext}`, onClick: nextTrack, "aria-label": "Next", children: _jsx(IconPlayerSkipForward, { size: 18 }) })] }), _jsxs("div", { className: styles.progress, children: [_jsx("span", { className: styles.timeLabel, children: formatTime(currentTime, currentTrack.duration) }), _jsx(Slider, { className: styles.progressSlider, value: currentTime, onChange: handleSeek, max: 100, "aria-label": "Seek" }), _jsx("span", { className: styles.timeLabel, children: formatDuration() })] }), hasInstrumental && (_jsx("div", { className: styles.modeToggle, children: _jsx(SegmentedControl, { value: playMode, onChange: (v) => void handleModeChange(v), ariaLabel: language === "ru" ? "Источник" : "Source", options: [
                                { value: "full", label: language === "ru" ? "Песня" : "Song" },
                                {
                                    value: "minus",
                                    label: minusResolving
                                        ? language === "ru"
                                            ? "Минус…"
                                            : "Minus…"
                                        : language === "ru"
                                            ? "Минус"
                                            : "Minus",
                                    disabled: minusResolving,
                                },
                            ] }) })), _jsxs("div", { className: styles.extra, children: [_jsxs("button", { type: "button", className: styles.shareBtn, onClick: handleShare, "aria-label": shareLabel, children: [_jsx(IconSend, { size: 15 }), _jsx("span", { children: shareLabel })] }), _jsx(Menu, { position: "top-end", minWidth: 180, trigger: _jsx("button", { type: "button", className: styles.iconBtn, "aria-label": "Menu", children: _jsx(IconDots, { size: 18 }) }), items: menuItems }), _jsx("button", { type: "button", className: styles.iconBtn, onClick: closePlayer, "aria-label": language === "ru" ? "Закрыть" : "Close", children: _jsx(IconX, { size: 18 }) })] })] }), _jsx(InstrumentalConfirmModal, { opened: confirmOpen, onClose: () => setConfirmOpen(false), onConfirm: () => startInstrumental(), language: language }), _jsx(RenameAudioModal, { opened: renameOpen, onClose: () => setRenameOpen(false), initialTitle: currentTrack.title, onSubmit: (title) => void handleRenameSubmit(title), language: language }), secondary.modals] }));
};
